import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Project, ProjectGrid } from '@/types/projects'
import useOverlayLocalStorage from '@/hooks/useOverlayLocalStorage'
import { getBaseGrid, getNonGridFields, sortBySortOrder } from '@/utils/field'
import DocumentFiltersProvider from '@/components/document-filters/DocumentFiltersProvider'
import DocumentDropZone from '@/components/drop-zone/DocumentDropZone'
import {
  DocumentRowsTable,
  DocumentTable,
} from '@/components/document-table/DashboardTable'
import { useFileUploadContext } from '@/components/upload-files/FileUploadProvider'
import ColumnVisibilityProvider from '@/components/workflows/ColumnVisibilityProvider'
import ColumnOrderProvider from '@/components/workflows/ColumnOrderProvider'
import ColumnSortingProvider from '@/components/workflows/ColumnSortingProvider'
import SelectedGridsProvider from '@/components/workflows/SelectedGridsProvider'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import SelectedWorkflowStateProvider, {
  useSelectedWorkflowStateContext,
} from '@/components/workflows/SelectedWorkflowStateProvider'
import { useRecentOrganizationsContext } from '@/components/organizations/RecentOrganizationsProvider'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import ProjectTablesFilterDrawer from './ProjectTablesFilterDrawer'

type ProjectTablesDisplayProps = {
  project: Project
}

function ProjectTablesDisplay({ project }: ProjectTablesDisplayProps) {
  const { view } = useParams()
  const [isFullscreen, setIsFullscreen] = useState(false)

  const drawer = useOverlayLocalStorage({
    initiallyOpen: true,
    localStorageKey: `project-dashboard-drawer-open-${project.id}`,
  })

  const { selectedWorkflow } = useSelectedWorkflowContext()

  const baseGridWithoutGridFields = useMemo(() => {
    const baseGrid = getBaseGrid(
      project.project_grids as ProjectGrid[],
    ) as ProjectGrid
    return {
      ...baseGrid,
      //TODO: Remove sorting for when the backend returns data sorted
      project_grid_fields: sortBySortOrder(
        getNonGridFields(baseGrid.project_grid_fields),
      ),
    }
  }, [project.project_grids])

  const { handleFilesSelectedToUpload, handleConfirmUploadFiles } =
    useFileUploadContext()

  const [recentOrgs] = useRecentOrganizationsContext()

  const { selectedWorkflowState, setWorkflowState } =
    useSelectedWorkflowStateContext()

  return (
    <ColumnOrderProvider>
      <ColumnSortingProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <SelectedGridsProvider
            baseGridWithoutGridFields={baseGridWithoutGridFields}
            grids={project.project_grids as ProjectGrid[]}
          >
            <ColumnVisibilityProvider
              baseGridNonGridFields={
                baseGridWithoutGridFields.project_grid_fields
              }
              selectedWorkflowState={selectedWorkflowState}
            >
              <DocumentFiltersProvider isDocumentView={view === 'documents'}>
                <ProjectTablesFilterDrawer
                  overlay={drawer}
                  workflowStates={selectedWorkflow?.workflow_states || []}
                  selectedWorkflowState={selectedWorkflowState}
                  setWorkflowState={setWorkflowState}
                  showAllFields={view === 'data'}
                />

                <DocumentDropZone
                  onDrop={handleFilesSelectedToUpload}
                  onOrgDrop={handleConfirmUploadFiles}
                  recentOrgs={recentOrgs}
                  isFullscreen={isFullscreen}
                >
                  <Box sx={{ flexGrow: 1, height: '100%' }}>
                    {view === 'documents' ? (
                      <DocumentTable
                        state={selectedWorkflowState}
                        workflow={selectedWorkflow}
                        isFullscreen={isFullscreen}
                        setIsFullscreen={setIsFullscreen}
                      />
                    ) : (
                      <DocumentRowsTable
                        state={selectedWorkflowState}
                        workflow={selectedWorkflow}
                        isFullscreen={isFullscreen}
                        setIsFullscreen={setIsFullscreen}
                      />
                    )}
                  </Box>
                </DocumentDropZone>
              </DocumentFiltersProvider>
            </ColumnVisibilityProvider>
          </SelectedGridsProvider>
        </LocalizationProvider>
      </ColumnSortingProvider>
    </ColumnOrderProvider>
  )
}

export default function ProjectTables() {
  const { project } = useProjectContext()

  return (
    <SelectedWorkflowStateProvider>
      <ProjectTablesDisplay project={project} />
    </SelectedWorkflowStateProvider>
  )
}
